import { useEffect, useState, type FormEvent } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { financeApi } from '@/api/finance.api';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import type { FinBusinessRule } from '@/types/finance';

export function FinanceBusinessRulesPage() {
  const [rules, setRules] = useState<FinBusinessRule[]>([]);
  const [ruleCode, setRuleCode] = useState('');
  const [ruleName, setRuleName] = useState('');
  const [entityType, setEntityType] = useState('INVOICE');
  const [amountThreshold, setAmountThreshold] = useState('10000');
  const [approverRole, setApproverRole] = useState('FINANCE_MANAGE');
  const [saving, setSaving] = useState(false);

  const load = () => {
    financeApi.listBusinessRules().then(setRules).catch((e) => showApiError(e, 'Failed to load rules'));
  };

  useEffect(() => {
    load();
  }, []);

  const saveRule = async (e: FormEvent) => {
    e.preventDefault();
    if (!ruleCode.trim() || !ruleName.trim()) return;
    setSaving(true);
    try {
      await financeApi.saveBusinessRule({
        ruleCode: ruleCode.trim(),
        ruleName: ruleName.trim(),
        entityType,
        conditionJson: {
          field: 'amount',
          operator: 'GT',
          value: Number(amountThreshold),
        },
        actionJson: {
          action: 'REQUIRE_APPROVAL',
          approverRoles: [approverRole],
        },
      });
      notify.success('Rule saved');
      setRuleCode('');
      setRuleName('');
      load();
    } catch (err) {
      showApiError(err, 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Business rules"
        description="Configure no-code IF/THEN rules for approvals — amount, role, and entity based"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Finance', to: '/finance' },
          { label: 'Administration', to: '/finance/control' },
          { label: 'Rules' },
        ]}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="card p-4">
          <h3 className="font-semibold mb-3">Active rules</h3>
          <ul className="text-sm space-y-2">
            {rules.map((r) => (
              <li key={r.id} className="border rounded p-2">
                <div className="font-medium">{r.ruleName}</div>
                <div className="text-xs text-slate-500">{r.ruleCode} · {r.entityType}</div>
              </li>
            ))}
            {rules.length === 0 && <p className="text-slate-500">No rules yet. Example: Invoice &gt; 10,000 → CFO approval.</p>}
          </ul>
        </div>

        <form className="card p-4 space-y-3" onSubmit={saveRule}>
          <h3 className="font-semibold">New rule</h3>
          <Input label="Rule code" value={ruleCode} onChange={(e) => setRuleCode(e.target.value)} placeholder="INV_CFO_10K" />
          <Input label="Rule name" value={ruleName} onChange={(e) => setRuleName(e.target.value)} placeholder="Large invoice needs CFO" />
          <Select
            label="When entity is"
            value={entityType}
            onChange={setEntityType}
            options={[
              { value: 'INVOICE', label: 'Invoice' },
              { value: 'EXPENSE', label: 'Expense' },
              { value: 'PAYMENT_RUN', label: 'Payment run' },
              { value: 'JOURNAL_ENTRY', label: 'Journal entry' },
            ]}
          />
          <Input
            type="number"
            label="IF amount greater than"
            value={amountThreshold}
            onChange={(e) => setAmountThreshold(e.target.value)}
          />
          <Input
            label="THEN require approver role"
            value={approverRole}
            onChange={(e) => setApproverRole(e.target.value)}
          />
          <Button type="submit" loading={saving}>Save rule</Button>
        </form>
      </div>
    </div>
  );
}
